import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Landmark, Search, ArrowLeft, ExternalLink, Loader2, ScrollText,
} from "lucide-react";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

export default function Fase6Legislativo() {
  const [source, setSource] = useState("camara"); // camara | senado
  const [ano, setAno] = useState(new Date().getFullYear());
  const [tema, setTema] = useState("");
  const [sigla, setSigla] = useState("");
  const [numero, setNumero] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doSearch = async () => {
    setBusy(true); setResult(null);
    try {
      const q = new URLSearchParams();
      if (ano) q.set("ano", ano);
      if (source === "camara") {
        if (tema) q.set("tema", tema);
        q.set("itens", 30);
        const { data } = await api.get(`/intel/camara/proposicoes?${q}`);
        setResult({ ...data, source: "camara" });
      } else {
        if (sigla) q.set("sigla", sigla);
        if (numero) q.set("numero", numero);
        if (tema) q.set("palavra", tema);
        const { data } = await api.get(`/intel/senado/materias?${q}`);
        setResult({ ...data, source: "senado" });
      }
    } catch (e) {
      toast.error("Erro ao consultar fonte oficial");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="fase6-legislativo-page">
      <header className="bg-[#0B1B2F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-3">
          <Link to="/inteligencia" data-testid="back-intel"
                className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4"/> Inteligência 360
          </Link>
          <span className="text-white/30">·</span>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: `${GOLD}33`, border: `1px solid ${GOLD}` }}>
              <Landmark className="w-4 h-4" style={{ color: GOLD }}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>
                Fase 6 · Legislativo
              </div>
              <h1 className="font-heading text-xl">Câmara & Senado · Proposições</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit">
            <button data-testid="tab-camara"
              onClick={() => setSource("camara")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition
                ${source==="camara" ? "bg-white shadow text-[#0B1B2F]" : "text-slate-600"}`}>
              Câmara dos Deputados
            </button>
            <button data-testid="tab-senado"
              onClick={() => setSource("senado")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition
                ${source==="senado" ? "bg-white shadow text-[#0B1B2F]" : "text-slate-600"}`}>
              Senado Federal
            </button>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Ano</label>
              <input type="number" value={ano} onChange={(e) => setAno(e.target.value)}
                data-testid="input-ano"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">
                {source === "camara" ? "Tema (keywords)" : "Palavra-chave"}
              </label>
              <input value={tema} onChange={(e) => setTema(e.target.value)}
                placeholder="ex: aposentadoria, tributário, digital"
                data-testid="input-tema"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
            </div>
            {source === "senado" ? (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Sigla</label>
                <input value={sigla} onChange={(e) => setSigla(e.target.value)}
                  placeholder="PL, PEC, MP…" data-testid="input-sigla"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
              </div>
            ) : (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Itens</label>
                <div className="mt-1 text-sm text-slate-400 px-1 py-2">30 primeiros</div>
              </div>
            )}
          </div>

          <button onClick={doSearch} disabled={busy}
            data-testid="btn-buscar-legislativo"
            className="mt-4 px-5 py-2.5 rounded-lg text-white text-sm font-semibold
                          disabled:opacity-60 inline-flex items-center gap-2"
            style={{ background: NAVY }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
            Buscar em fonte oficial
          </button>
        </div>

        {result && (
          <section className="mt-6" data-testid="results-legislativo">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-heading text-2xl" style={{ color: NAVY }}>
                {result.count || 0} proposição(ões) encontradas
              </h2>
              <div className="text-xs text-slate-500">{result.source_name}</div>
            </div>
            {(result.records || []).length === 0 ? (
              <div className="bg-white rounded-xl border p-6 text-sm text-slate-500 text-center">
                Nenhum resultado para os filtros informados.
              </div>
            ) : (
              <div className="space-y-2">
                {result.records.map((r, i) => (
                  <div key={r.id || i} data-testid={`prop-${i}`}
                       className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                      <ScrollText className="w-5 h-5 mt-0.5 shrink-0" style={{ color: GOLD }}/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {result.source === "camara"
                              ? `${r.sigla_tipo} ${r.numero}/${r.ano}`
                              : r.identificacao || `${r.sigla} ${r.numero}/${r.ano}`}
                          </span>
                          {r.autor && (
                            <span className="text-xs text-slate-500 truncate">{r.autor}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
                          {r.ementa || "—"}
                        </p>
                        {(r.url_detalhe || r.url) && (
                          <a href={r.url_detalhe || r.url} target="_blank" rel="noreferrer"
                             className="text-xs mt-2 inline-flex items-center gap-1 hover:underline"
                             style={{ color: GOLD }}>
                            Ver na fonte oficial <ExternalLink className="w-3 h-3"/>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
